/**
 * Vehicle Specifications Tabs Component
 * Displays Overview, Performance, Running Costs tabs for vehicle listings
 * Admin-configurable tabs with enable/disable per tab
 */
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useCountry } from '../context/CountryContext';
import { 
  Car, Gauge, Wallet, Fuel, Calendar, 
  Settings2, Zap, Leaf, Shield, DollarSign 
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICONS = {
  car: Car,
  gauge: Gauge,
  wallet: Wallet,
  fuel: Fuel,
  calendar: Calendar,
  settings: Settings2,
  zap: Zap,
  leaf: Leaf,
  shield: Shield,
  dollar: DollarSign
};

// Default tab configurations (fallback)
const DEFAULT_TABS = {
  overview: {
    tab_key: 'overview',
    name: 'Overview',
    icon: 'car',
    enabled: true,
    order: 1,
    fields: [
      { key: 'year', label: 'Year', type: 'text' },
      { key: 'colour', label: 'Colour', type: 'text' },
      { key: 'mileage', label: 'Mileage', type: 'mileage' },
      { key: 'seats', label: 'Seats', type: 'number' },
      { key: 'body_type', label: 'Body Type', type: 'text' },
      { key: 'doors', label: 'Doors', type: 'number' },
      { key: 'transmission', label: 'Transmission', type: 'text' },
      { key: 'luggage_capacity', label: 'Luggage Capacity (seats up)', type: 'text' }
    ]
  },
  performance: {
    tab_key: 'performance',
    name: 'Performance',
    icon: 'gauge',
    enabled: true,
    order: 2,
    fields: [
      { key: 'engine_size', label: 'Engine Size', type: 'text' },
      { key: 'engine_power', label: 'Engine Power', type: 'text' },
      { key: 'torque', label: 'Torque', type: 'text' },
      { key: 'acceleration', label: '0-60 mph', type: 'text' },
      { key: 'top_speed', label: 'Top Speed', type: 'text' },
      { key: 'drive_type', label: 'Drive Type', type: 'text' },
      { key: 'fuel_type', label: 'Fuel Type', type: 'text' },
      { key: 'cylinders', label: 'Cylinders', type: 'text' }
    ]
  },
  running_costs: {
    tab_key: 'running_costs',
    name: 'Running Costs',
    icon: 'wallet',
    enabled: true,
    order: 3,
    fields: [
      { key: 'fuel_economy', label: 'Better fuel economy', type: 'fuel' },
      { key: 'insurance_group', label: 'Lower insurance group', type: 'text' },
      { key: 'tax_per_year', label: 'Tax per year', type: 'currency' },
      { key: 'co2_emissions', label: 'CO₂ emissions', type: 'text' },
      { key: 'euro_emissions', label: 'Euro Emissions Standard', type: 'text' },
      { key: 'service_cost', label: 'Annual Service Cost', type: 'currency' }
    ]
  }
};

const VehicleSpecsTabs = ({ listing }) => {
  const { formatCurrency, formatMileage, country } = useCountry();
  const [tabsConfig, setTabsConfig] = useState(DEFAULT_TABS);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Fetch tab configuration from admin settings
  useEffect(() => {
    const fetchTabsConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/vehicles/listing-tabs/config`, {
          params: { country }
        });
        if (response.data.success && response.data.tabs) {
          setTabsConfig(response.data.tabs);
        }
      } catch (error) {
        console.debug('Using default tabs config:', error);
        // Use defaults on error
      } finally {
        setLoading(false);
      }
    };

    fetchTabsConfig();
  }, [country]);

  // Get enabled tabs sorted by order
  const enabledTabs = Object.values(tabsConfig)
    .filter(tab => tab.enabled)
    .sort((a, b) => a.order - b.order);

  // Set first enabled tab as active if current is disabled
  useEffect(() => {
    if (enabledTabs.length > 0) {
      const currentTabEnabled = enabledTabs.find(t => t.tab_key === activeTab);
      if (!currentTabEnabled) {
        setActiveTab(enabledTabs[0].tab_key);
      }
    }
  }, [enabledTabs, activeTab]);

  // Format value based on type
  const formatValue = (value, type, fieldKey) => {
    if (!value && value !== 0) return '-';

    switch (type) {
      case 'mileage':
        return formatMileage(value);
      case 'currency':
        return formatCurrency(value);
      case 'fuel':
        // Format as fuel economy (mpg or L/100km based on country)
        return country === 'UK' || country === 'EU' 
          ? `${value} mpg` 
          : `${value} km/L`;
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      default:
        return value;
    }
  };

  // Get value from listing with fallback paths
  const getListingValue = (fieldKey) => {
    // Try direct field
    if (listing[fieldKey] !== undefined) return listing[fieldKey];
    
    // Try nested in vehicle_details
    if (listing.vehicle_details?.[fieldKey] !== undefined) {
      return listing.vehicle_details[fieldKey];
    }
    
    // Try nested in specs
    if (listing.specs?.[fieldKey] !== undefined) {
      return listing.specs[fieldKey];
    }
    
    // Try camelCase version
    const camelKey = fieldKey.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
    if (listing[camelKey] !== undefined) return listing[camelKey];
    
    // Specific mappings
    const mappings = {
      colour: listing.color || listing.colour,
      body_type: listing.bodyType || listing.body_type,
      fuel_type: listing.fuelType || listing.fuel_type,
      drive_type: listing.driveType || listing.drive_type,
      engine_size: listing.engineSize || listing.engine_size || listing.engine,
      engine_power: listing.enginePower || listing.power || listing.horsepower,
      top_speed: listing.topSpeed || listing.top_speed,
      tax_per_year: listing.roadTax || listing.tax_per_year || listing.annualTax,
      insurance_group: listing.insuranceGroup || listing.insurance_group,
      co2_emissions: listing.co2 || listing.emissions || listing.co2_emissions,
      fuel_economy: listing.mpg || listing.fuelEconomy || listing.fuel_economy,
      luggage_capacity: listing.luggageCapacity || listing.bootSpace || listing.luggage_capacity
    };
    
    return mappings[fieldKey];
  };

  // Check if any field in a tab has data
  const tabHasData = (tab) => {
    return tab.fields.some(field => {
      const value = getListingValue(field.key);
      return value !== undefined && value !== null && value !== '';
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-gray-200 rounded w-2/3"></div>
            <div className="grid grid-cols-2 gap-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-8 bg-gray-100 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (enabledTabs.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Tab List */}
          <TabsList className="grid w-full mb-6" style={{ 
            gridTemplateColumns: `repeat(${enabledTabs.length}, 1fr)` 
          }}>
            {enabledTabs.map(tab => {
              const IconComponent = ICONS[tab.icon] || Car;
              const hasData = tabHasData(tab);
              
              return (
                <TabsTrigger 
                  key={tab.tab_key} 
                  value={tab.tab_key}
                  className="flex items-center gap-2 data-[state=active]:border-b-2 data-[state=active]:border-green-500"
                  disabled={!hasData}
                >
                  <IconComponent className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.name}</span>
                  {!hasData && (
                    <Badge variant="outline" className="text-xs ml-1">No data</Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Tab Contents */}
          {enabledTabs.map(tab => (
            <TabsContent key={tab.tab_key} value={tab.tab_key} className="mt-0">
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                {tab.fields.map((field, index) => {
                  const value = getListingValue(field.key);
                  const formattedValue = formatValue(value, field.type, field.key);
                  
                  // Skip empty fields
                  if (formattedValue === '-') return null;
                  
                  return (
                    <div key={field.key} className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 text-sm">{field.label}</span>
                      <span className="font-medium text-gray-900">{formattedValue}</span>
                    </div>
                  );
                })}
              </div>
              
              {/* Show message if no data for this tab */}
              {!tabHasData(tab) && (
                <div className="text-center py-8 text-gray-500">
                  <p>No {tab.name.toLowerCase()} information available for this listing.</p>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default VehicleSpecsTabs;
